import React, { FC, useMemo } from "react";
import { useSelector } from "react-redux";

import { ConnectionProblem } from "./Dialogs/ConnectionProblem/ConnectionProblem";
import { Dialogs } from "./Dialogs/Dialogs";
import { Layout } from "./Layout/Layout";
import { SharedLayout } from "./Layout/SharedLayout";
import { GlobalsContainerStyled } from "./Layout/styled";
import { DashboardProvider } from "../modules/Dashboard/components/DashboardContext";
import { Module, RoutedModule } from "./Module";
import { MouseLabel } from "./MouseLabel/MouseLabel";
import { Loader } from "./Loader";
import { LayerFilter } from "./LayerFilter/LayerFilter";
import { GeometrySelectionTools } from "../modules/Map/components/GeometrySelectionTools/GeometrySelectionTools";
import { PresentationTools } from "../modules/Map/components/PresentationTools/PresentationTools";
import ChatControl from "../modules/MapControls/ChatControl.module";
import { Chat } from "./Chat";

import { getAppConfigShared } from "../ducks/appConfig";
import { getBottomHeight, getBottomState } from "../ducks/layout/selectors";
import { getLayerTempFilterTool } from "../ducks/layerTempFilter/selectors";
import { useConnectionProblemEffect } from "../hooks/api/useConnectionProblemEffect";
import { useServerConnectionEffect } from "../hooks/api/useServerConnectionEffect";
import { useLastProjectsEffect } from "../hooks/api/useLastProjectsEffect";
import { useModules } from "../hooks/appConfig/useModules";
import { useGeometrySelectionHistory } from "../hooks/geometrySelection/useGeometrySelectionHistory";
import { useFetchProjectLayerInfos } from "../hooks/project/useProjectLayers";
import { useOnProjectChange } from "../hooks/project/useOnProjectChange";
import { useInitPresentationMode } from "../hooks/layout/useInitPresentationMode";
import { useProjectSettings } from "../hooks/project/useProjectSettings";

import { login } from "../resources/user";

import { GeometrySelectionContext } from "./context";

import { ClientModule, ModuleName } from "../appConfig/types";

const Modules: FC<{ modules: ClientModule[] }> = ({ modules }) => {
  return (
    <>
      {modules.map(({ submodules, ...module }, index) => {
        const { route } = module;

        return route ? (
          <RoutedModule key={`${module.name}-${index}`} route={route} {...module}>
            {!!submodules && submodules.length > 0 && <Modules modules={submodules} />}
          </RoutedModule>
        ) : (
          <Module key={`${module.name}-${index}`} {...module}>
            {!!submodules && submodules.length > 0 && <Modules modules={submodules} />}
          </Module>
        );
      })}
    </>
  );
};

export const App: FC = () => {
  const {
    drawFeature,
    addFeature,
    replaceFeature,
    setFeatures,
    stepBack,
    stepForward,
    purgeFeatures,
    saveEditState,
    layer: geometrySelectionLayer,
    bufferLayer: geometrySelectionBufferLayer,
    editLayer: geometrySelectionEditLayer,
  } = useGeometrySelectionHistory();

  const shared = useSelector(getAppConfigShared);
  const modules = useModules();
  const bottomState = useSelector(getBottomState);
  const bottomHeight = useSelector(getBottomHeight);
  const layerFilterTool = useSelector(getLayerTempFilterTool);
  const { loading, error, data: isLoggedSuccess } = login.use();

  const {
    main = [],
    bottom = [],
    panel = [],
    presentation = [],
    portal = [],
    mainControls = [],
    objectCard = [],
    topControls = [],
    additionalControls = [],
  } = modules;

  const processedMainModules = useMemo(() => {
    const mapModule = main.find(item => item.name === ModuleName.Map);

    return mapModule ? [mapModule, ...main.filter(item => item.name !== ModuleName.Map)] : main;
  }, [main]);

  const processedPanelModules = useMemo(() => {
    const projectPanelModule = panel.find(item => item.name === ModuleName.ProjectPanel);

    return projectPanelModule
      ? [projectPanelModule, ...panel.filter(item => item.name !== ModuleName.ProjectPanel)]
      : panel;
  }, [panel]);

  const processedPresentationModules = useMemo(() => {
    const projectPresentationModule = presentation.find(item => item.name === ModuleName.ProjectConfiguration);

    return projectPresentationModule
      ? [projectPresentationModule, ...presentation.filter(item => item.name !== ModuleName.ProjectConfiguration)]
      : presentation;
  }, [presentation]);

  const commonLayoutProps = useMemo(
    () => ({
      main: (
        <>
          <Modules modules={processedMainModules} />
          <Modules modules={objectCard} />
          <Chat />
        </>
      ),
      panel: layerFilterTool.active ? <LayerFilter /> : <Modules modules={processedPanelModules} />,
      presentation: <Modules modules={processedPresentationModules} />,
      mainControls: (
        <>
          <Modules modules={mainControls} />
          <ChatControl />
        </>
      ),
      topControls: (
        <>
          <Modules modules={topControls} />
          <GeometrySelectionTools />
          <PresentationTools />
        </>
      ),
    }),
    [
      layerFilterTool.active,
      mainControls,
      objectCard,
      processedPresentationModules,
      processedMainModules,
      processedPanelModules,
      topControls,
    ],
  );

  const layoutProps = useMemo(
    () => ({
      bottom: <Modules modules={bottom} />,
      additionalControls: <Modules modules={additionalControls} />,
      portal: (
        <>
          <Modules modules={portal} />
          <ConnectionProblem />
          <Dialogs />
        </>
      ),
      bottomState,
      bottomHeight,
    }),
    [additionalControls, bottom, bottomHeight, bottomState, portal],
  );

  useServerConnectionEffect(loading, error, isLoggedSuccess);
  useConnectionProblemEffect();
  useLastProjectsEffect(!loading && isLoggedSuccess);
  useFetchProjectLayerInfos();
  useProjectSettings();
  useOnProjectChange();
  useInitPresentationMode();

  return (
    <GeometrySelectionContext.Provider
      value={{
        drawFeature,
        addFeature,
        replaceFeature,
        setFeatures,
        stepBack,
        stepForward,
        purgeFeatures,
        saveEditState,
        layer: geometrySelectionLayer,
        bufferLayer: geometrySelectionBufferLayer,
        editLayer: geometrySelectionEditLayer,
      }}
    >
      <DashboardProvider>
        <GlobalsContainerStyled>
          {shared ? (
            <SharedLayout {...commonLayoutProps} {...layoutProps} />
          ) : (
            <Layout {...commonLayoutProps} {...layoutProps} />
          )}
          <MouseLabel />
        </GlobalsContainerStyled>
      </DashboardProvider>
      {loading && <Loader fixed style={{ height: "100%", top: 0, zIndex: 20 }} />}
    </GeometrySelectionContext.Provider>
  );
};
